import React, { useState, useEffect } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { ProgressSpinner } from 'primereact/progressspinner';
import { format, isToday, isAfter, differenceInMinutes } from 'date-fns';
import globalUserPage from '../../../../../stores/global-user-page';
import { ClassSchedule } from '../../../../../api/types/programs/course/exams/response/exam-metadata';
import dateUtils from '../../../../../utils/date-format';

// Types
export interface Program {
  id: number;
  name: string;
  startYear: number;
  programNumberInYear: number;
  branch: string;
}

export interface Course {
  id: number;
  name: string;
}

/*export interface Lecture {
  id: number;
  name: string | null;

  date: Date;
  startTime: string;
  endTime: string;
  classroom: string | null;
  course: Course;
  program: Program;
  status: 'upcoming' | 'in-progress' | 'completed';
}*/

interface Lecture {
  id: number;
  name: string;
  startTime: string;
  endTime: string;
  classId: number;
  classroom: string;
  courseId: number;
  courseName: string;
  programId: number;
  programName: string;
  programStartYear: number;
  programNumberInYear: number;
  branch: string;
  branchId: number;
  actualStartTime: string | null;
  actualEndTime: string | null;
}

export const UserPageLecturerLectures = () => {
  //const [lectures, setLectures] = useState<Lecture[]>([]);
  const [selectedLecture, setSelectedLecture] = useState<Lecture | null>(null);
  const [visible, setVisible] = useState(false);

  const lectures = globalUserPage.lecturerDetails.lectures;

  const todayLectures = lectures.filter((lecture) =>
    isToday(lecture.startTime),
  );
  const upcomingLectures = lectures.filter((lecture) =>
    isAfter(lecture.endTime, new Date()),
  );

  const getTimeDifference = (lectureDate: Date, startTime: string) => {
    const [hours, minutes] = startTime.split(':').map(Number);
    const lectureStartTime = new Date(lectureDate);
    lectureStartTime.setHours(hours, minutes, 0, 0);

    const now = new Date();
    const minutesUntilStart = differenceInMinutes(lectureStartTime, now);

    if (minutesUntilStart <= 60 && minutesUntilStart > 0) {
      return `בעוד ${minutesUntilStart} דקות`;
    }
    return null;
  };

  const lectureDetailsTemplate = (lecture: Lecture) => {
    return (
      <div className="flex flex-column gap-2">
        <div className="flex align-items-center gap-2">
          <span className="font-bold">תוכנית:</span>
          <span>{lecture.programName}</span>
        </div>
        <div className="flex align-items-center gap-2">
          <span className="font-bold">קורס:</span>
          <span>{lecture.courseName}</span>
        </div>
        <div className="flex align-items-center gap-2">
          <span className="font-bold">כיתה:</span>
          <span>{lecture.classroom}</span>
        </div>
        <div className="flex align-items-center gap-2">
          <span className="font-bold">תאריך:</span>
          <span>{dateUtils.displayFormatDate(lecture.startTime)}</span>
        </div>

        <div className="flex align-items-center gap-2">
          <span className="font-bold">שעות:</span>
          <span>
            {dateUtils.formatTimeRangeBetweenDates(
              lecture.startTime,
              lecture.endTime,
            )}
          </span>
        </div>
      </div>
    );
  };

  const actionButtonsTemplate = (lecture: Lecture) => {
    return (
      <div className="flex gap-2">
        {lecture.status === 'upcoming' && (
          <Button
            label="התחל הרצאה"
            icon="pi pi-play"
            severity="success"
            onClick={() => {
              // Handle start lecture
              const updatedLectures = lectures.map((l) =>
                l.id === lecture.id
                  ? { ...l, status: 'in-progress' as const }
                  : l,
              );
              setLectures(updatedLectures);
            }}
          />
        )}

        {lecture.status === 'in-progress' && (
          <Button
            label="סיים הרצאה"
            icon="pi pi-stop"
            severity="danger"
            onClick={() => {
              // Handle finish lecture
              const updatedLectures = lectures.map((l) =>
                l.id === lecture.id
                  ? { ...l, status: 'completed' as const }
                  : l,
              );
              setLectures(updatedLectures);
            }}
          />
        )}
      </div>
    );
  };

  const getRowClassName = (rowData: any) => {
    if (isToday(rowData.date)) {
      return 'imminent-lecture';
    }
    return '';
  };

  /*if (loading) {
    return (
      <div
        className="flex justify-content-center align-items-center"
        style={{ height: '100vh' }}
      >
        <ProgressSpinner />
      </div>
    );
  }*/

  return (
    <div className="p-4">
      <div className="grid">
        <div className="col-12 mb-4">
          <h2 className="mb-3">הרצאות היום</h2>
          <div className="w-full overflow-x-auto">
            <DataTable
              value={todayLectures}
              emptyMessage={
                <div style={{ textAlign: 'right' }}>אין הרצאות עתידיות</div>
              }
              className="p-datatable-striped w-full"
              scrollable
              scrollHeight="flex"
              rowClassName={getRowClassName}
            >
              <Column
                field="name"
                header="שם ההרצאה"
                style={{ minWidth: '200px', textAlign: 'right' }}
              />
              <Column
                field="courseName"
                header="קורס"
                style={{ minWidth: '200px', textAlign: 'right' }}
              />
              <Column
                field="programName"
                header="תוכנית"
                style={{ minWidth: '200px', textAlign: 'right' }}
              />
              <Column
                field="programStartYear"
                header="שנת התחלה"
                style={{ minWidth: '120px', textAlign: 'right' }}
                body={(rowData) => rowData.programStartYear}
              />
              <Column
                field="programNumberInYear"
                header="מספר תוכנית בשנה"
                style={{ minWidth: '150px', textAlign: 'right' }}
                body={(rowData) => rowData.programNumberInYear}
              />

              <Column
                field="startTime"
                header="תאריך"
                style={{ minWidth: '100px', textAlign: 'right' }}
                body={(rowData: ClassSchedule) => {
                  return (
                    <label>
                      {dateUtils.displayFormatDate(rowData.startTime)}
                    </label>
                  );
                }}
              />
              <Column
                field="startTime"
                header="שעת התחלה"
                style={{ minWidth: '100px', textAlign: 'right' }}
                body={(rowData: ClassSchedule) => (
                  <label>
                    {dateUtils.formatTimeFromDate(rowData.startTime)}
                  </label>
                )}
              />
              <Column
                field="endTime"
                header="שעת סיום"
                style={{ minWidth: '100px', textAlign: 'right' }}
                body={(rowData: ClassSchedule) => (
                  <label>{dateUtils.formatTimeFromDate(rowData.endTime)}</label>
                )}
              />
              <Column
                header="פרטים"
                style={{ minWidth: '100px', textAlign: 'right' }}
                body={(rowData) => (
                  <Button
                    icon="pi pi-info-circle"
                    onClick={() => {
                      setSelectedLecture(rowData);
                      setVisible(true);
                    }}
                  />
                )}
              />
            </DataTable>
          </div>
        </div>

        <div className="col-12">
          <h2 className="mb-3">הרצאות עתידיות</h2>
          <div className="w-full overflow-x-auto">
            <DataTable
              value={upcomingLectures}
              emptyMessage={
                <div style={{ textAlign: 'right' }}>אין הרצאות עתידיות</div>
              }
              className="p-datatable-striped w-full"
              scrollable
              scrollHeight="flex"
            >
              <Column
                field="name"
                header="שם ההרצאה"
                style={{ minWidth: '200px', textAlign: 'right' }}
              />
              <Column
                field="courseName"
                header="קורס"
                style={{ minWidth: '200px', textAlign: 'right' }}
              />
              <Column
                field="programName"
                header="תוכנית"
                style={{ minWidth: '200px', textAlign: 'right' }}
              />
              <Column
                field="programStartYear"
                header="שנת התחלה"
                style={{ minWidth: '120px', textAlign: 'right' }}
                body={(rowData) => rowData.programStartYear}
              />
              <Column
                field="programNumberInYear"
                header="מספר תוכנית בשנה"
                style={{ minWidth: '150px', textAlign: 'right' }}
                body={(rowData) => rowData.programNumberInYear}
              />

              <Column
                field="startTime"
                header="תאריך"
                style={{ minWidth: '100px', textAlign: 'right' }}
                body={(rowData: ClassSchedule) => {
                  return (
                    <label>
                      {dateUtils.displayFormatDate(rowData.startTime)}
                    </label>
                  );
                }}
              />
              <Column
                field="startTime"
                header="שעת התחלה"
                style={{ minWidth: '100px', textAlign: 'right' }}
                body={(rowData: ClassSchedule) => (
                  <label>
                    {dateUtils.formatTimeFromDate(rowData.startTime)}
                  </label>
                )}
              />
              <Column
                field="endTime"
                header="שעת סיום"
                style={{ minWidth: '100px', textAlign: 'right' }}
                body={(rowData: ClassSchedule) => (
                  <label>{dateUtils.formatTimeFromDate(rowData.endTime)}</label>
                )}
              />
              <Column
                header="פרטים"
                style={{ minWidth: '100px', textAlign: 'right' }}
                body={(rowData) => (
                  <Button
                    icon="pi pi-info-circle"
                    onClick={() => {
                      setSelectedLecture(rowData);
                      setVisible(true);
                    }}
                  />
                )}
              />
            </DataTable>
          </div>
        </div>
      </div>

      <Dialog
        visible={visible}
        onHide={() => setVisible(false)}
        header="פרטי הרצאה"
        style={{ width: '50vw' }}
      >
        {selectedLecture && lectureDetailsTemplate(selectedLecture)}
      </Dialog>

      <style>
        {`
          .imminent-lecture {
            background-color: var(--yellow-50) !important;
          }
          .imminent-lecture:hover {
            background-color: var(--yellow-100) !important;
          }
        `}
      </style>
    </div>
  );
};
